import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, XCircle, AlertCircle, X, ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';

/**
 * Toast notification component
 */
const Toast = ({ message, type = 'info', duration = 5000, onClose, updated }) => {
  // Auto-dismiss timer
  useEffect(() => {
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose, updated]);

  const icons = {
    success: <CheckCircle size={20} className="text-green-400" />,
    error: <XCircle size={20} className="text-red-400" />,
    info: <AlertCircle size={20} className="text-blue-400" />,
    warning: <AlertCircle size={20} className="text-amber-400" />
  };

  const bgColors = {
    success: 'bg-green-500/10 border-green-500/30',
    error: 'bg-red-500/10 border-red-500/30',
    info: 'bg-blue-500/10 border-blue-500/30',
    warning: 'bg-amber-500/10 border-amber-500/30'
  };

  return (
    <div 
      style={{ maxWidth: '500px', wordBreak: 'break-word' }}
      className={`px-4 py-3 rounded-lg border shadow-lg ${bgColors[type]}`}
    >
      <div className="flex items-start gap-3">
        <div style={{ flexShrink: 0 }}>{icons[type]}</div>
        <div style={{ flex: 1, wordBreak: 'break-word', whiteSpace: 'normal', overflow: 'visible' }}>
          <span className="text-sm text-white">{message}</span>
        </div>
        <button
          onClick={onClose}
          style={{ flexShrink: 0 }}
          className="p-1 text-gray-400 hover:text-white rounded"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

/**
 * Toast container that manages multiple toasts
 */
export const ToastContainer = ({ toasts, removeToast }) => {
  if (toasts.length === 0) return null;

  return createPortal(
    <div className="fixed bottom-4 right-4 z-[10001] space-y-2">
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          updated={toast.updated}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>,
    document.body
  );
};

/**
 * Hook for managing toast notifications
 */
export const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'info', duration = 5000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type, duration }]);
    return id;
  };

  const updateToast = (id, message, type, duration = 5000) => {
    setToasts(prev => prev.map(t => 
      t.id === id ? { ...t, message, type, duration, updated: Date.now() } : t
    ));
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const toast = {
    success: (message, duration) => addToast(message, 'success', duration),
    error: (message, duration) => addToast(message, 'error', duration),
    info: (message, duration) => addToast(message, 'info', duration),
    warning: (message, duration) => addToast(message, 'warning', duration),
    update: updateToast
  };

  return { toasts, toast, removeToast };
};

export default Toast;

